import { useCallback, useEffect, useState } from 'react'

import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ErrorIcon from '@mui/icons-material/Error'
import Search from '@mui/icons-material/Search'
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Container,
	IconButton,
	InputAdornment,
	MenuItem,
	OutlinedInput,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
} from '@mui/material'
import {
	DataGrid,
	GridColDef,
	GridPaginationModel,
	GridRenderCellParams,
} from '@mui/x-data-grid'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { UserRole } from 'constants/enums/UserRolesEnum'

import { UserResponse, UsersMetadata } from 'models/userModels'

import { MODAL_TYPES, useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import { getAllUsers, patchUser } from 'services/admin.service'

import SubPageHeader from 'components/SubPageHeader'

import { styles } from './Admin.styles'

export const TEST_ID = 'admin-page'

type UserStatus = 'Account Created' | 'Setup Pending' | 'Invite Expired'

const USER_ROLES = Object.keys(UserRole) as (keyof typeof UserRole)[]

interface EditValues {
	firstName: string
	lastName: string
	role: string
}

const StatusChip = ({ status }: { status: UserStatus }) => {
	if (status === 'Account Created') {
		return (
			<Chip
				icon={<CheckIcon sx={styles.statusChipAccountCreatedIcon} />}
				label="Account Created"
				variant="outlined"
				size="small"
				sx={styles.statusChipAccountCreated}
			/>
		)
	}
	if (status === 'Invite Expired') {
		return (
			<Chip
				icon={<ErrorIcon sx={styles.statusChipInviteExpiredIcon} />}
				label="Invite Expired"
				variant="outlined"
				size="small"
				sx={styles.statusChipInviteExpired}
			/>
		)
	}
	return (
		<Chip
			icon={<ErrorIcon sx={styles.statusChipSetupPendingIcon} />}
			label="Setup Pending"
			variant="outlined"
			size="small"
			sx={styles.statusChipSetupPending}
		/>
	)
}

const AdminPage = () => {
	const { showModal, store } = useModalContext()
	const { showAlert } = useToastContext()
	const [isAdminPageAuthorized, setIsAdminPageAuthorized] = useState(false)
	const [rows, setRows] = useState<UserResponse[]>([])
	const [metadata, setMetadata] = useState<UsersMetadata>({
		totalCount: 0,
		page: 1,
		pageSize: 10,
	})
	const [loading, setLoading] = useState(false)
	const [editRowId, setEditRowId] = useState<number | null>(null)
	const [editValues, setEditValues] = useState<EditValues | null>(null)
	const [isSaving, setIsSaving] = useState(false)

	const fetchUsers = useCallback(
		async (page: number, pageSize: number) => {
			setLoading(true)
			try {
				const response = await getAllUsers({ page, pageSize })
				setRows(response.data.records)
				setMetadata(response.data.metadata)
			} catch {
				showAlert?.({
					title: 'Uh oh, error saving your changes',
					severity: ToastSeverity.ERROR,
				})
			} finally {
				setLoading(false)
			}
		},
		[showAlert]
	)

	useEffect(() => {
		if (
			isAdminPageAuthorized ||
			store.modalType === MODAL_TYPES.ADMIN_PAGE_ACCESS_MODAL
		) {
			return
		}

		showModal(MODAL_TYPES.ADMIN_PAGE_ACCESS_MODAL, {
			onAuthorized: () => setIsAdminPageAuthorized(true),
			disableBackdropClose: true,
			backdropBlur: 55,
		})
	}, [isAdminPageAuthorized, showModal, store.modalType])

	useEffect(() => {
		if (isAdminPageAuthorized) {
			fetchUsers(metadata.page, metadata.pageSize)
		}
		// Only fetch on authorization or pagination change, not when metadata object ref changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdminPageAuthorized, fetchUsers])

	const isEditValid =
		editValues !== null &&
		editValues.firstName.trim().length >= 1 &&
		editValues.lastName.trim().length >= 1 &&
		USER_ROLES.includes(editValues.role as keyof typeof UserRole)

	const handleEditClick = (row: UserResponse) => {
		setEditRowId(row.userId)
		setEditValues({
			firstName: row.firstName ?? '',
			lastName: row.lastName ?? '',
			role: row.role,
		})
	}

	const handleCancelEdit = () => {
		setEditRowId(null)
		setEditValues(null)
	}

	const handleSaveEdit = async () => {
		if (!editValues || editRowId === null) return
		setIsSaving(true)
		try {
			const response = await patchUser(editRowId, {
				firstName: editValues.firstName,
				lastName: editValues.lastName,
				role: editValues.role,
			})
			setRows((prev) =>
				prev.map((r) => (r.userId === editRowId ? response.data : r))
			)
			setEditRowId(null)
			setEditValues(null)
			showAlert?.({
				objectType: 'updateUser',
				severity: ToastSeverity.SUCCESS,
			})
		} catch {
			showAlert?.({
				objectType: 'updateUserError',
				severity: ToastSeverity.ERROR,
			})
		} finally {
			setIsSaving(false)
		}
	}

	const handleDeleteClick = (email: string) => {
		showModal(MODAL_TYPES.REMOVE_LICENSE_MODAL, { userEmail: email })
	}

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		// MUI DataGrid uses 0-based page; API uses 1-based
		fetchUsers(model.page + 1, model.pageSize)
	}

	const columns: GridColDef[] = [
		{
			field: 'checkbox',
			headerName: '',
			width: 50,
			sortable: false,
			renderCell: () => <Checkbox size="medium" />,
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			flex: 1,
			minWidth: 120,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserResponse
				if (row.userId === editRowId && editValues) {
					return (
						<TextField
							variant="standard"
							value={editValues.firstName}
							inputProps={{
								'data-testid': 'admin-edit-first-name-input',
							}}
							sx={styles.editTextField}
							onChange={(e) =>
								setEditValues((prev) =>
									prev
										? { ...prev, firstName: e.target.value }
										: prev
								)
							}
						/>
					)
				}
				return <span>{row.firstName}</span>
			},
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			flex: 1,
			minWidth: 120,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserResponse
				if (row.userId === editRowId && editValues) {
					return (
						<TextField
							variant="standard"
							value={editValues.lastName}
							inputProps={{
								'data-testid': 'admin-edit-last-name-input',
							}}
							sx={styles.editTextField}
							onChange={(e) =>
								setEditValues((prev) =>
									prev
										? { ...prev, lastName: e.target.value }
										: prev
								)
							}
						/>
					)
				}
				return <span>{row.lastName}</span>
			},
		},
		{
			field: 'email',
			headerName: 'User Email',
			flex: 1,
			minWidth: 160,
			renderCell: (params: GridRenderCellParams) => (
				<Box
					component="a"
					href={`mailto:${params.value}`}
					sx={styles.emailLink}
				>
					{params.value}
				</Box>
			),
		},
		{
			field: 'userStatus',
			headerName: 'User Status',
			flex: 1,
			minWidth: 160,
			renderCell: (params: GridRenderCellParams) => {
				const status = params.value as UserStatus | undefined
				if (!status) return null
				return <StatusChip status={status} />
			},
		},
		{
			field: 'role',
			headerName: 'User Role',
			flex: 1,
			minWidth: 140,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserResponse
				if (row.userId === editRowId && editValues) {
					return (
						<Select
							variant="standard"
							value={editValues.role}
							sx={styles.editSelect}
							MenuProps={{ disableScrollLock: true }}
							onChange={(e: SelectChangeEvent) =>
								setEditValues((prev) =>
									prev
										? { ...prev, role: e.target.value }
										: prev
								)
							}
						>
							{USER_ROLES.map((role) => (
								<MenuItem
									key={role}
									value={role}
									sx={styles.editSelectMenuItem}
								>
									{role}
								</MenuItem>
							))}
						</Select>
					)
				}
				return <span>{row.role}</span>
			},
		},
		{
			field: 'actions',
			headerName: 'Change Details',
			width: 160,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as UserResponse
				if (row.userId === editRowId) {
					return (
						<Box sx={styles.cancelSaveCell}>
							<Button
								data-testid="admin-edit-cancel-button"
								size="small"
								variant="text"
								onClick={handleCancelEdit}
							>
								CANCEL
							</Button>
							<Button
								data-testid="admin-edit-save-button"
								size="small"
								variant="contained"
								color="secondary"
								disabled={!isEditValid || isSaving}
								onClick={handleSaveEdit}
							>
								SAVE
							</Button>
						</Box>
					)
				}
				return (
					<Box sx={styles.actionsCell}>
						<IconButton
							size="medium"
							sx={styles.actionsCellIcon}
							onClick={() => handleEditClick(row)}
						>
							<EditIcon fontSize="medium" />
						</IconButton>
						<IconButton
							size="medium"
							sx={styles.actionsCellIcon}
							onClick={() => handleDeleteClick(row.email)}
						>
							<DeleteIcon fontSize="medium" />
						</IconButton>
					</Box>
				)
			},
		},
	]

	if (!isAdminPageAuthorized) {
		return <Stack data-testid={TEST_ID} sx={styles.StackContainer} />
	}

	return (
		<Stack data-testid={TEST_ID} sx={styles.StackContainer}>
			<SubPageHeader
				title="User Management"
				subtitle="Manage Brainstorm teams and users here"
				backgroundGradient="linear-gradient(90deg, #9DCDF2 0%, #BDDEF6 100%)"
			/>

			<Box sx={styles.ContentBox}>
				<Container maxWidth={'xl'} sx={{ ...styles.list }}>
					<Box sx={styles.SearchRow}>
						<OutlinedInput
							startAdornment={
								<InputAdornment position="start">
									<IconButton>
										<Search />
									</IconButton>
								</InputAdornment>
							}
							placeholder="Search..."
							inputProps={{ 'data-testid': 'admin-search-input' }}
							sx={styles.searchBarStyle}
						/>
						<Button
							data-testid="admin-add-new-users-button"
							color="secondary"
							variant="outlined"
							size="medium"
							sx={styles.addNewUsersButton}
						>
							Add New Users
						</Button>
					</Box>

					<Box sx={styles.dataGridWrapper}>
						<DataGrid
							rows={rows}
							columns={columns}
							getRowId={(row) => row.userId}
							paginationMode="server"
							rowCount={metadata.totalCount}
							paginationModel={{
								page: metadata.page - 1,
								pageSize: metadata.pageSize,
							}}
							onPaginationModelChange={
								handlePaginationModelChange
							}
							pageSizeOptions={[10, 25, 50]}
							loading={loading}
							disableRowSelectionOnClick
							sx={styles.dataGrid}
						/>
					</Box>
				</Container>
			</Box>
		</Stack>
	)
}

export default AdminPage
